import React, { useState, useEffect } from "react";
import axios from "axios";
import AppBar from "@mui/material/AppBar";
import List from "@mui/material/List";
import Grid from "@mui/material/Grid";
import { Input, Button, Select } from "antd";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import RoleCountrySettingsSection from "./RoleCountrySettingsSection";

const apiUrl = process.env.REACT_APP_API_Roles;
const countriesManualApi = (process.env.REACT_APP_API_Countries_Manual || process.env.REACT_APP_API_UpdatedCountries || "").trim();
const countriesSpecialApi = (process.env.REACT_APP_API_Countries_Special || process.env.REACT_APP_API_UpdatedCountries || "").trim();
const applicationsApi = process.env.REACT_APP_API_AllApps;

const validateRoleConfig = (roleConfig) => {
  const countrySettings = roleConfig?.country_settings ?? [];
  const batches = roleConfig?.batches ?? [];
  if (!Array.isArray(countrySettings) || countrySettings.length === 0) {
    return "Please add at least one country";
  }
  if (!Array.isArray(batches) || batches.length === 0) {
    return "Please configure at least one batch";
  }
  const batch1 = batches.find((b) => b.batch_number === 1);
  if (!batch1 || batch1.otps_to_unlock_next !== 0) {
    return "Batch 1 must have OTPs to unlock next = 0";
  }
  for (const cs of countrySettings) {
    if (
      (cs.apps_mode === "only_selected" || cs.apps_mode === "except_selected") &&
      (!cs.application_ids || cs.application_ids.length === 0)
    ) {
      return `${cs.country_name || cs.country_id}: select at least one app when using only/except selected`;
    }
  }
  return null;
};

const AddRole = ({ setOpen }) => {
  const [title, setTitle] = useState("");
  const [roleConfig, setRoleConfig] = useState({ country_settings: [], batches: [] });
  const [countries, setCountries] = useState([]);
  const [applications, setApplications] = useState([]);
  const [userDb, setUserDb] = useState("");
  const [errors, setErrors] = useState({
    title: "",
    user_db: "",
    country_settings: "",
    general: "",
  });
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [loadingCountries, setLoadingCountries] = useState(false);

  const parseCountriesResponse = (data) => {
    if (!data) return [];
    let raw = data?.body ?? data;
    if (typeof raw === "string") {
      try {
        raw = JSON.parse(raw);
      } catch {
        return [];
      }
    }
    const arr = Array.isArray(raw) ? raw : raw?.body ?? raw?.data;
    return Array.isArray(arr) ? arr : [];
  };

  useEffect(() => {
    const fetchApps = async () => {
      setLoadingData(true);
      try {
        const appsRes = await axios.get(applicationsApi);
        const appsBody = (() => {
          const d = appsRes?.data;
          const raw = d?.body ?? d;
          if (typeof raw === "string") {
            try {
              const parsed = JSON.parse(raw);
              return parsed?.body ?? parsed ?? [];
            } catch {
              return [];
            }
          }
          return Array.isArray(raw) ? raw : raw?.body ?? [];
        })();
        setApplications(Array.isArray(appsBody) ? appsBody : []);
      } catch (err) {
        console.error("Error fetching applications:", err);
      } finally {
        setLoadingData(false);
      }
    };
    fetchApps();
  }, []);

  useEffect(() => {
    if (!userDb) {
      setCountries([]);
      return;
    }
    const fetchCountries = async () => {
      setLoadingCountries(true);
      try {
        const api = userDb === "special" ? countriesSpecialApi : countriesManualApi;
        const res = await axios.get(api.trim());
        const body = parseCountriesResponse(res?.data);
        setCountries(body);
        setRoleConfig({ country_settings: [], batches: [] });
      } catch (err) {
        console.error("Error fetching countries:", err);
        setCountries([]);
      } finally {
        setLoadingCountries(false);
      }
    };
    fetchCountries();
  }, [userDb]);

  const handleTitleChange = (e) => {
    setTitle(e.target.value);
    setErrors((prev) => ({ ...prev, title: "" }));
  };

  const buildPayload = () => {
    const country_settings = (roleConfig?.country_settings ?? []).map((cs) => ({
      country_id: cs.country_id,
      apps_mode: cs.apps_mode || "all",
      application_ids: cs.application_ids || [],
    }));
    const batches = (roleConfig?.batches ?? []).map((b) => ({
      batch_number: b.batch_number,
      otps_to_unlock_next: b.otps_to_unlock_next ?? 0,
      country_sims: Object.entries(b.country_sims || {}).map(([country_id, sims_to_lock]) => ({
        country_id: Number(country_id),
        sims_to_lock: sims_to_lock ?? 0,
      })),
    }));
    return { country_settings, batches };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = { title: "", user_db: "", country_settings: "", general: "" };

    if (!title.trim()) {
      newErrors.title = "Please enter the role title";
    }
    if (!userDb || (userDb !== "manual" && userDb !== "special")) {
      newErrors.user_db = "Please select which database users for this role will be created in";
    }

    const csError = validateRoleConfig(roleConfig);
    if (csError) {
      newErrors.country_settings = csError;
    }

    if (newErrors.title || newErrors.user_db || newErrors.country_settings) {
      setErrors(newErrors);
      return;
    }

    try {
      setLoading(true);
      const { country_settings, batches } = buildPayload();
      const payload = {
        title: title.trim(),
        user_db: userDb,
        is_tester: 1,
        country_settings,
        batches,
      };
      const response = await axios.post(apiUrl, payload);
      if (response.status === 200) {
        setOpen(false);
      } else {
        throw new Error("Failed to add role");
      }
    } catch (error) {
      console.error("Error adding role:", error);
      setErrors({
        ...errors,
        general: error.response?.data?.errorMsg || "Failed to add role",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <AppBar position="relative">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flex: 1 }}>
            Add Role
          </Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={() => setOpen(false)}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <List style={{ height: "100vh" }}>
        <Grid
          container
          style={{ height: "100%", justifyContent: "center" }}
          alignItems="flex-start"
        >
          <Grid item xs={12} sm={10} md={8} lg={8}>
            <form
              onSubmit={handleSubmit}
              className="mx-10"
              style={{ paddingBottom: 24 }}
            >
              <div className="mb-4">
                <h2 className="text-2xl font-bold">Add Role</h2>
              </div>
              <div className="grid grid-cols-1 gap-4 mb-4">
                <Input
                  value={title}
                  onChange={handleTitleChange}
                  placeholder="Enter Role Title"
                  addonBefore="Role Title"
                  className="mb-4"
                />
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">
                    User Database <span className="text-red-500">*</span>
                  </label>
                  <Select
                    value={userDb || undefined}
                    onChange={(v) => {
                      setUserDb(v);
                      setErrors((prev) => ({ ...prev, user_db: "" }));
                      setRoleConfig({ country_settings: [], batches: [] });
                    }}
                    placeholder="Select database for users in this role"
                    style={{ width: "100%" }}
                    dropdownStyle={{ zIndex: 9999 }}
                    options={[
                      { value: "manual", label: "Manual" },
                      { value: "special", label: "Special" },
                    ]}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Cannot be changed later. Users created with this role will be stored in the selected database.
                  </p>
                  {errors.user_db && (
                    <p className="text-red-500 text-xs mt-1">{errors.user_db}</p>
                  )}
                </div>
              </div>
              {errors.title && (
                <p className="text-red-500 text-xs">{errors.title}</p>
              )}

              {!userDb ? (
                <p style={{ color: "#64748b" }}>
                  Select a User Database above to configure countries and batches.
                </p>
              ) : loadingData || loadingCountries ? (
                <p style={{ color: "#64748b" }}>
                  Loading countries and applications...
                </p>
              ) : (
                <RoleCountrySettingsSection
                  countries={countries}
                  applications={applications}
                  value={roleConfig}
                  onChange={setRoleConfig}
                  errors={errors}
                />
              )}

              {errors.general && (
                <p className="text-red-500 text-xs mt-2">{errors.general}</p>
              )}
              <div className="flex justify-end" style={{ marginTop: 24 }}>
                <Button type="primary" htmlType="submit" loading={loading}>
                  Add Role
                </Button>
              </div>
            </form>
          </Grid>
        </Grid>
      </List>
    </>
  );
};

export default AddRole;
